<template>
  <div>
    <!-- 标题背景色选择器（原有部分） -->
    <div class="control-group">
      <label for="title-bg-color">取色板背景色:</label>
      <input 
        type="color" 
        id="title-bg-color" 
        v-model="titleBgColor"
        @change="updateFromColorPicker"
      >
    </div>
    
    <!-- 取色板工具 -->
    <div class="tool-content">
      <h1>RGB/HEX 取色器</h1>
      
      <div class="color-preview" :style="{ backgroundColor: currentColor }"></div>
      
      <div class="input-container">
        <div class="input-group">
          <label for="rgbInput">RGB:</label>
          <input 
            type="text" 
            id="rgbInput" 
            v-model="rgbInputValue" 
            placeholder="rgb(0, 0, 0)"
            @change="updateFromRgbInput"
            @keypress.enter="updateFromRgbInput"
          >
          <button class="copy-btn" @click="copyToClipboard(rgbInputValue)">复制</button>
          <div class="error" v-if="rgbError">{{ rgbError }}</div>
        </div>
        
        <div class="input-group">
          <label for="hexInput">HEX:</label>
          <input 
            type="text" 
            id="hexInput" 
            v-model="hexInputValue" 
            placeholder="#000000"
            @change="updateFromHexInput"
            @keypress.enter="updateFromHexInput"
          >
          <button class="copy-btn" @click="copyToClipboard(hexInputValue)">复制</button>
          <div class="error" v-if="hexError">{{ hexError }}</div>
        </div>
      </div>
      
      <div class="slider-container">
        <label for="redSlider">红:</label>
        <input 
          type="range" 
          id="redSlider" 
          min="0" 
          max="255" 
          v-model.number="redValue"
          @input="updateFromSliders"
        >
        <span class="value-display">{{ redValue }}</span>
      </div>
      
      <div class="slider-container">
        <label for="greenSlider">绿:</label>
        <input 
          type="range" 
          id="greenSlider" 
          min="0" 
          max="255" 
          v-model.number="greenValue"
          @input="updateFromSliders"
        >
        <span class="value-display">{{ greenValue }}</span>
      </div>
      
      <div class="slider-container">
        <label for="blueSlider">蓝:</label>
        <input 
          type="range" 
          id="blueSlider" 
          min="0" 
          max="255" 
          v-model.number="blueValue"
          @input="updateFromSliders"
        >
        <span class="value-display">{{ blueValue }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ColorPicker',
  data() {
    return {
      titleBgColor: '#000000', // 原有的颜色选择器绑定的值
      redValue: 0,
      greenValue: 0,
      blueValue: 0,
      rgbInputValue: '',
      hexInputValue: '',
      rgbError: '',
      hexError: ''
    }
  },
  computed: {
    currentColor() {
      return `rgb(${this.redValue}, ${this.greenValue}, ${this.blueValue})`;
    }
  },
  watch: {
    // 监听计算属性currentColor的变化，同步到titleBgColor
    currentColor(newColor) {
      this.titleBgColor = this.rgbToHex(this.redValue, this.greenValue, this.blueValue);
    },
    // 监听原有颜色选择器的变化
    titleBgColor(newColor) {
      this.updateFromHex(newColor);
    }
  },
  methods: {
    // 从原有颜色选择器更新
    updateFromColorPicker() {
      this.updateFromHex(this.titleBgColor);
    },
    
    // 直接从HEX值更新
    updateFromHex(hex) {
      if (!this.isValidHex(hex)) {
        this.hexError = '无效的HEX格式';
        return;
      }
      
      const rgb = this.hexToRgb(hex);
      if (!rgb) {
        this.hexError = '无效的HEX颜色';
        return;
      }
      
      this.redValue = rgb.r;
      this.greenValue = rgb.g;
      this.blueValue = rgb.b;
      
      this.rgbInputValue = `rgb(${this.redValue}, ${this.greenValue}, ${this.blueValue})`;
      this.hexInputValue = hex;
      this.clearErrors();
    },
    
    updateFromSliders() {
      this.rgbInputValue = this.currentColor;
      this.hexInputValue = this.rgbToHex(this.redValue, this.greenValue, this.blueValue);
      this.clearErrors();
    },
    
    updateFromRgbInput() {
      if (!this.isValidRgb(this.rgbInputValue)) {
        this.rgbError = '无效的RGB格式，应为 rgb(255, 255, 255)';
        return;
      }
      
      const rgb = this.parseRgb(this.rgbInputValue);
      if (!rgb || rgb.r > 255 || rgb.g > 255 || rgb.b > 255) {
        this.rgbError = 'RGB值应在0-255之间';
        return;
      }
      
      this.redValue = rgb.r;
      this.greenValue = rgb.g;
      this.blueValue = rgb.b;
      
      this.hexInputValue = this.rgbToHex(this.redValue, this.greenValue, this.blueValue);
      this.clearErrors();
    },
    
    updateFromHexInput() {
      if (!this.isValidHex(this.hexInputValue)) {
        this.hexError = '无效的HEX格式，应为 #FFFFFF 或 #FFF';
        return;
      }
      
      const rgb = this.hexToRgb(this.hexInputValue);
      if (!rgb) {
        this.hexError = '无效的HEX颜色';
        return;
      }
      
      this.redValue = rgb.r;
      this.greenValue = rgb.g;
      this.blueValue = rgb.b;
      
      this.rgbInputValue = `rgb(${this.redValue}, ${this.greenValue}, ${this.blueValue})`;
      this.clearErrors();
    },
    
    rgbToHex(r, g, b) {
      return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
    },
    
    hexToRgb(hex) {
      hex = hex.replace('#', '');
      
      if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
      }
      
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      
      return { r, g, b };
    },
    
    parseRgb(rgbStr) {
      const match = rgbStr.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i);
      if (!match) return null;
      
      return {
        r: parseInt(match[1]),
        g: parseInt(match[2]),
        b: parseInt(match[3])
      };
    },
    
    isValidHex(hex) {
      return /^#?([0-9A-F]{3}){1,2}$/i.test(hex);
    },
    
    isValidRgb(rgb) {
      return /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/i.test(rgb);
    },
    
    clearErrors() {
      this.rgbError = '';
      this.hexError = '';
    },
    
    copyToClipboard(text) {
      navigator.clipboard.writeText(text);
      
      // 通知父页面显示提示（如果需要）
      if (window.parent && window.parent.showNotification) {
        window.parent.showNotification('已复制到剪贴板');
      }
    }
  },
  mounted() {
    // 初始化颜色
    this.updateFromHex(this.titleBgColor);
  }
}
</script>

<style scoped>
/* 保留原有样式 */
body {
  font-family: Arial, sans-serif;
  margin: 0;
  padding: 0;
  background-color: white;
}

.tool-content {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

h1 {
  color: #333;
  text-align: center;
  margin-bottom: 20px;
}

.color-preview {
  width: 200px;
  height: 200px;
  margin: 20px auto;
  border: 1px solid #ccc;
  border-radius: 10px;
  transition: background-color 0.3s;
}

.input-container {
  margin: 20px 0;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background-color: #f9f9f9;
}

.input-group {
  margin: 10px 0;
}

.input-group label {
  display: inline-block;
  width: 80px;
  text-align: right;
}

.input-group input {
  padding: 5px;
  width: 100px;
  margin-left: 10px;
}

.slider-container {
  margin: 15px 0;
}

.slider-container label {
  display: inline-block;
  width: 50px;
  text-align: right;
  margin-right: 10px;
}

.value-display {
  display: inline-block;
  width: 40px;
  text-align: left;
  margin-left: 10px;
}

.copy-btn {
  padding: 8px 15px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-left: 10px;
}

.copy-btn:hover {
  background-color: #45a049;
}

.error {
  color: red;
  font-size: 12px;
  margin-top: 5px;
  height: 15px;
}

/* 原有颜色选择器的样式 */
.control-group {
  margin-bottom: 15px;
}

.control-group label {
  display: inline-block;
  width: 140px;
}

.control-group input[type="color"] {
  vertical-align: middle;
  margin-left: 10px;
}
</style>